import { compact, merge, uniq } from "lodash";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import Subscription from "../../types/Subscription";
import { ModelEventConstructor } from "../../utils/ModelEventConstructor";
import {
	NonEditableProperties,
	NonEditablePropertyNames,
} from "../../types/NonEditableProperties";
import { ModelId } from "../../types/ModelId";
import {
	Base,
	TaxiServiceOld as TaxiService,
	Language,
	CarClass,
	ExecutorGroup,
} from "..";

const logger = createLogger({ name: "ExecutorApp" });

export function applyMandatoryExecutorRegistrationFieldToggles(
	settings: ExecutorApp.Model.RegistrationSettings,
): ExecutorApp.Model.RegistrationSettings {
	const mandatory: ExecutorApp.Model.RegistrationFieldToggle = {
		active: true,
		required: true,
	};
	return {
		...settings,
		executor: {
			...settings.executor,
			name: { ...mandatory },
			phones: { ...mandatory },
			login: { ...mandatory },
			password: { ...mandatory },
		},
	};
}

function getDefaultRegistrationSettings(): ExecutorApp.Model.RegistrationSettings {
	const off: ExecutorApp.Model.RegistrationFieldToggle = {
		active: false,
		required: false,
	};
	const mandatory: ExecutorApp.Model.RegistrationFieldToggle = {
		active: true,
		required: true,
	};
	return {
		active: false,
		executor: {
			active: false,
			name: { ...mandatory },
			surname: { ...off },
			fatherName: { ...off },
			phones: { ...mandatory },
			emails: { ...off },
			birthday: { ...off },
			login: { ...mandatory },
			password: { ...mandatory },
			passport: {
				active: false,
				series: { ...off },
				number: { ...off },
				issuedAt: { ...off },
				issuedBy: { ...off },
				birthPlace: { ...off },
				residencePlace: { ...off },
				livingAddress: { ...off },
				taxId: { ...off },
				files: { ...off },
			},
			driverLicense: {
				active: false,
				series: { ...off },
				number: { ...off },
				category: { ...off },
				issuedAt: { ...off },
				generalDrivingExperience: { ...off },
				files: { ...off },
			},
			bankCard: {
				active: false,
				number: { ...off },
				expiresAt: { ...off },
				note: { ...off },
			},
			individualProprietor: {
				active: false,
				series: { ...off },
				certificateNumber: { ...off },
				registeredAt: { ...off },
				usreou: { ...off },
				iban: { ...off },
				files: { ...off },
			},
			personalFiles: { ...off },
			licenseFiles: { ...off },
			otherFiles: { ...off },
		},
		car: {
			active: false,
			modelToBodyType: { ...off },
			color: { ...off },
			seats: { ...off },
			manufactureYear: { ...off },
			registrationNumber: { ...off },
			vehicleNumber: { ...off },
			transportOwnership: { ...off },
			registrationCertificate: { ...off },
			licenseCard: { ...off },
			avatarFiles: { ...off },
			registrationCertificateFiles: { ...off },
			licenseCardFiles: { ...off },
			insuranceFiles: { ...off },
			otherFiles: { ...off },
		},
	};
}

class ExecutorApp extends Base {
	public static fromResponse(data: any): ExecutorApp.Model {
		const taxiServices = data.executorAppToTaxiServices.flatMap(
			(item) => item?.taxiService,
		);

		const taxiServiceIds = uniq<number>(
			taxiServices.map((item) => item?.id),
		);
		const companyIds = uniq<number>(
			taxiServices.map((item) => item?.company?.id),
		);

		const carClasses = data.executorAppToCarClasses.flatMap(
			(item) => item?.carClass,
		);

		const carClassIds = uniq<number>(carClasses?.map((item) => item?.id));

		return {
			id: data.id,
			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			deletedAt: data.deletedAt,

			name: data.name,
			active: data.active,
			isExecutorGroup: data.isExecutorGroup,
			root: data.root,
			base: data.base,

			mainSettings: data.mainSettings,
			orderSettings: data.orderSettings,
			interfaceSettings: data.interfaceSettings
				? {
						...data.interfaceSettings,
						menuFunctions: {
							...data.interfaceSettings.menuFunctions,
							executor: data.interfaceSettings.menuFunctions
								.executor
								? data.interfaceSettings.menuFunctions.executor
								: {
										active: false,
										allowChangeServices: { active: false },
										allowChangeCarClassBroadcast: {
											active: false,
										},
										allowChangeCarClassFreeWait: {
											active: false,
										},
										allowChangePaymentMethod: {
											active: false,
											list: ["cash", "nonCash", "card"],
										},
								  },
						},
				  }
				: data.interfaceSettings,
			taximeter: data.taximeter,
			registrationSettings:
				applyMandatoryExecutorRegistrationFieldToggles(
					merge(
						getDefaultRegistrationSettings(),
						data.registrationSettings ?? {},
					),
				),

			taxiServices,
			taxiServiceIds,

			carClasses,
			carClassIds,

			executorGroups: data.executorGroups || [],

			companyIds,
		};
	}

	public static toRequest(
		model: ExecutorApp.New | ExecutorApp.Modified,
	): any {
		const carClassIds = model.carClassIds?.length
			? model.carClassIds
			: undefined;

		return {
			name: model.name,
			active: model.active,
			isExecutorGroup: model.isExecutorGroup,

			mainSettings: model.mainSettings,
			orderSettings: model.orderSettings,
			taximeter: model.taximeter,
			interfaceSettings: model.interfaceSettings,
			registrationSettings: model.registrationSettings
				? applyMandatoryExecutorRegistrationFieldToggles(
						model.registrationSettings,
				  )
				: undefined,
			carClassIds,
			taxiServiceIds: model.taxiServiceIds,
		};
	}

	public static async store(object: ExecutorApp.New, force?: boolean) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.executorApp.create(
						ExecutorApp.toRequest(object),
						force,
					),
				{ silent: false, error: true },
			);

			logger.info("ExecutorApp store", { object, res });
			return res;
		} catch (err: any) {
			if (err.message.includes("force")) return false;
			return true;
		}
	}

	public static async copy(id: number) {
		try {
			await this.request((prpc) => prpc.theirsModel.executorApp.copy(id));
		} catch (err: any) {
			throw new Error("Error in copy method:", err);
		}
	}

	public static async update(object: ExecutorApp.Modified, force?: boolean) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.executorApp.update(
						object.id,
						ExecutorApp.toRequest(object),
						force,
					),
				{ silent: false, error: true },
			);

			logger.info("ExecutorApp update", { res, object });

			if (res?.id) return this.fromResponse(res);

			return res;
		} catch (err: any) {
			if (err.message.includes("force")) return false;
			return true;
		}
	}

	public static async destroy(ids: number[] | number) {
		this.request((prpc) => prpc.theirsModel.executorApp.delete(ids));
	}

	public static async getGpsSpoofing(): Promise<ExecutorApp.GpsSpoofingState | null> {
		try {
			return await this.request((prpc) =>
				prpc.theirsModel.executorApp.getGpsSpoofing(),
			);
		} catch (err) {
			logger.error(err);
			return null;
		}
	}

	public static async setGpsSpoofing(
		active: boolean,
	): Promise<ExecutorApp.GpsSpoofingState | null> {
		try {
			return await this.request((prpc) =>
				prpc.theirsModel.executorApp.setGpsSpoofing({ active }),
			);
		} catch (err) {
			logger.error(err);
			return null;
		}
	}

	/** Переключения подмены GPS в филиалах диспетчера (в том числе другими диспетчерами). */
	public static async subscribeGpsSpoofing(onChange: () => void) {
		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.executorApp.subscribeGpsSpoofing({
					ping: () => true,
					onEvent: () => onChange(),
					onError: (error) => {
						logger.error(error);
					},
				}),
			{ name: "ExecutorApp.subscribeGpsSpoofing" },
		);

		return { unsubscribe: () => subscription.unsubscribe() };
	}

	public static async subscribe(
		params: ExecutorApp.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<ExecutorApp.Model>,
	): Promise<Subscription<ExecutorApp.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEventConstructor({
			onUpdate: (state) => {
				logger.info("[ExecutorApp] subscribe", { params, state });
				const items = compact(state?.models || []);
				onUpdate({
					...state,

					models: items.map(this.fromResponse),
				});
			},
		});

		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.executorApp.subscribe({
					params,
					ping: () => true,
					onEvent: (events) => {
						logger.info("[ExecutorApp] events", { events });
						modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error(error);
					},
				}),
			{ name: "ExecutorApp.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (options: ExecutorApp.SubscribeOptions) =>
				subscription.update(options),
		} as Subscription<ExecutorApp.SubscribeOptions>;
	}
}

export enum OrderPointsDisplay {
	ONLY_FIRST_POINT = "only_first_point_of_the_order",
	FIRST_AND_LAST_POINT = "first_and_last_point_of_the_order",
	ALL_POINTS = "all_points_of_the_order",
}

export enum DistanceCalculationMethod {
	/** select - `По прямой` */
	STRAIGHT_LINE = "straight_line",
	/** select - `OSM` */
	OSM = "osm",
}

export enum CarArriveTimeMethodEnum {
	DYNAMIC = "dynamic",
	STATIC = "static",
}

export enum MenuTypeOrderProcessingFunctions {
	/** Додати адресу */
	ADD_ADDRESS = "addAddress",
	/** Запізнення */
	DELAY = "delay",
	/** Зняття з замовлення */
	CANCEL_ORDER = "cancelOrder",
	/** Відмова клієнта */
	CLIENT_REFUSAL = "clientRefusal",
	/** Не виходять */
	STAY_OUTSIDE = "stayOutside",
	/** Стар таксиметре */
	STAR_TAXIMETER = "starTaximeter",
	/** Вести время простоя */
	ENTER_TIME_DOWNTIME = "enterTimeDowntime",
	/** Связь с диспетчером */
	CALL_TO_DISPATCHER = "callToDispatcher",
}

export const ExecutorAppPaymentMethod: ExecutorApp.Model.PaymentMethodType[] = [
	"cash",
	"nonCash",
	"card",
	"bonuses",
	"agents",
];

declare namespace ExecutorApp {
	interface Model extends NonEditableProperties {
		name: Record<Language, string>;
		active: boolean;
		isExecutorGroup: boolean;
		/** not use */
		default?: boolean;
		readonly root?: boolean;
		readonly base?: boolean;

		interfaceSettings: ExecutorApp.Model.InterfaceSettings;
		mainSettings: ExecutorApp.Model.MainSettings;
		orderSettings: ExecutorApp.Model.OrderSettings;
		taximeter: ExecutorApp.Model.TaximeterSettings;
		registrationSettings: ExecutorApp.Model.RegistrationSettings;

		/** only to front */
		carClasses: CarClass.Model[];
		/** if `undefined` no changed,  if `[]` removed all data or make update */
		carClassIds: number[];

		/** only to front */
		taxiServices: TaxiService.Model[];
		/** if `undefined` no changed , if `[]` removed all data or make update */
		taxiServiceIds: number[];

		executorGroups: ExecutorGroup.Model[];

		/** only to front */
		companyIds: number[];
	}

	type New = Omit<
		Model,
		| NonEditablePropertyNames
		| "taxiServices"
		| "carClasses"
		| "companyIds"
		| "executorGroups"
	>;
	type Modified = Partial<New> & { id: ModelId };

	interface GpsSpoofingState {
		/** Филиалы, где включён раздел «Подмена GPS» приложения исполнителя */
		items: {
			taxiServiceId: number;
			active: boolean;
			since: number | null;
		}[];
	}

	interface SubscribeOptions
		extends ServiceSubscribeOptionsBase<
			Pick<ExecutorApp.Model, "name" | "active" | "default" | "createdAt">
		> {
		name?: string;
		active?: boolean;
		isExecutorGroup?: boolean;
		default?: boolean;

		taxiServiceIds?: number[];
		carClassIds?: number[];
	}

	namespace Model {
		type PaymentMethodType =
			| "cash"
			| "nonCash"
			| "card"
			| "bonuses"
			| "agents";

		interface MainSettings {
			carClasses: {
				/** `toggle` - Car class */
				active: boolean;
			};
			/** `input` - number (ms) - Interval for GPS coordinates transmission from the driver */
			gpsTransmissionInterval: number;

			/**
			 * `input` - number (m) - Radius for displaying orders in the free filter by default for the driver
			 * @deprecated
			 * */
			defaultRadiusForFreeFormOrders: number;
			/** `input` - number (m) - Interval for transmitting distances from the driver to the pickup address */
			distanceTransmissionInterval: number;
			/** `input` - number (km/h) - Minimum speed for requesting a new distance from the driver to the pickup address */
			minimumSpeedToRequestNewDistance: number;
			/** `select` - Method for calculating the distance from the driver to the pickup address */
			distanceCalculationMethod: DistanceCalculationMethod;
		}

		interface OrderSettingsGeneral {
			/** `toggle` - Show order details to the driver always */
			showOrderDetailsAlways: boolean;
			/** `toggle` - Show order details to the driver after changing the order status to 'Accepted' */
			showOrderDetailsAfterChangeToInProcess: boolean;
			/** `toggle` - Show order details to the driver after changing the order status to 'On site' */
			showOrderDetailsAfterChangeToOnSite: boolean;
			/** `toggle` - Show order details to the driver after changing the order status to 'Met' */
			showOrderDetailsAfterChangeToMet: boolean;
		}

		interface Settings {
			/** `toggle` - Pre-order time */
			orderTime: boolean;
			/** `toggle` - Order cost (Until allowed to use `splitOrderCost`) */
			orderCost: boolean;

			/** `radio button` - `true` Separate order cost (cash + non-cash), `false` Total order cost */
			splitOrderCost: boolean;

			/** `toggle` - Distance to service */
			distanceBeforeServing: boolean;
			/** `toggle` - Time before service */
			timeBeforeServing: boolean;
			/** `toggle` - Service time */
			servingTime: boolean;

			/** `toggle` - Markup coefficient */
			markupCoefficient: boolean;
			/** `toggle` - Stock exchange indicator `todo disable` */
			stockExchangeFlag: boolean;
			/** `toggle` - Transport class */
			transportClass: boolean;
			/** `toggle` - Order notes */
			orderNotes: boolean;

			/** `toggle` - Payment method */
			paymentMethod: boolean;
			/** `toggle` - Average cost per km */
			averageCostPerKm: boolean;
			/** `toggle` - Client rating */
			clientRating: boolean;
			/** `toggle` - Services */
			services: boolean;

			/** `toggle` - Route length */
			routeLength: boolean;
			/** `toggle` - Number of client trips */
			clientTrips: boolean;
			/** `toggle` - Sectors */
			sector: boolean;
			/** `toggle` - Client name */
			clientName: boolean;

			/** `toggle` - Order points */
			orderPoints: boolean;
			/** `select` - Order points */
			orderPointsSelect: OrderPointsDisplay;
		}

		interface OrderSettingsFreeForm
			extends Omit<
				Settings,
				"splitOrderCost" | "timeBeforeServing" | "servingTime"
			> {}
		interface OrderSettingsMandatoryOrder
			extends Omit<Settings, "timeBeforeServing" | "servingTime"> {}
		interface OrderSettingsMyOrders
			extends Omit<
				Settings,
				"distanceBeforeServing" | "averageCostPerKm" | "routeLength"
			> {}
		interface OrderSettingsInProgress
			extends Pick<
				Settings,
				| "orderCost"
				| "splitOrderCost"
				| "timeBeforeServing"
				| "servingTime"
				| "paymentMethod"
				| "clientRating"
				| "services"
				| "markupCoefficient"
				| "clientTrips"
				| "sector"
				| "clientName"
				| "orderNotes"
				| "transportClass"
				| "orderPoints"
				| "orderPointsSelect"
				| "stockExchangeFlag"
				| "orderTime"
			> {}

		interface OrderSettings {
			general: OrderSettingsGeneral;
			freeForm: OrderSettingsFreeForm;
			mandatoryOrder: OrderSettingsMandatoryOrder;
			myOrders: OrderSettingsMyOrders;
			inProgress: OrderSettingsInProgress;
		}

		interface TaximeterSettings extends Record<string, any> {
			/** not use */
			allowEnableDisableBarredTariff: boolean;
			/** not use */
			allowSwitchToTaximeterDuringOrderWithMultiplePoints: boolean;

			allowUseRoundup: boolean;
			allowUseTaximeter: boolean;
			allowUseTaximeterForSpecialOrders: boolean;

			registerOwnOrdersForTaximeter: boolean;
			displaySumCalculatedByTaximeter: boolean;

			// startTaximeterAfterGo: boolean;
			startTaximeterAfterMeeting: boolean;
			startTaximeterAfterOrderAcceptance: boolean;
			startTaximeterAfterMeetingBackdrop: boolean;
			startTaximeterAfterOrderAcceptanceBackdrop: boolean;

			taximeterCanBePaused: boolean;
			/** not use */
			taximeterCanBeSetToUnpausable: boolean;
			taximeterWorksForAllOrdersInNormalMode: boolean;
			// taximeterWorksOnlyForOrdersFromOnePoint: boolean;

			allowManualTaximeterStartInOrder: boolean;
			allowServiceSelectionForOwnOrders: boolean;
			allowChangeTransportClass: boolean;
			allowCountryModeToggleBothWays: boolean;

			sendOrderData: boolean;
		}

		interface InterfaceSettings extends Record<string, any> {
			statusChange: InterfaceSettings.StatusChange;
			menuFunctions: InterfaceSettings.MenuFunctions;
			orderProcessingFunctions: InterfaceSettings.OrderProcessingFunctions;
			orderFilter: InterfaceSettings.OrderFilter;
			orderSorting: InterfaceSettings.OrderSorting;
			parking: InterfaceSettings.Parking;
			chain: InterfaceSettings.Chain;
			communication: InterfaceSettings.Communication;
			performerIncome: InterfaceSettings.PerformerIncome;
			ownOrderSettings: InterfaceSettings.OwnOrderSettings;
			ratingOrder: InterfaceSettings.RatingOrder;
			balance: InterfaceSettings.Balance;
			sos: InterfaceSettings.SOS;
			gpsSpoofing: InterfaceSettings.GpsSpoofing;
		}

		namespace InterfaceSettings {
			interface ExecutorSettings {
				active: boolean;
				allowChangeServices: { active: boolean };
				allowChangeCarClassBroadcast: { active: boolean };
				allowChangeCarClassFreeWait: { active: boolean };
				allowChangePaymentMethod: {
					active: boolean;
					list: PaymentMethodType[];
				};
			}
			interface HomeSettings {
				active: boolean;
			}

			interface ArchiveSettings {
				active: boolean;
			}

			interface MessageSettings {
				active: boolean;
				chat: { active: boolean };
				allowDispatchMessages: { active: boolean };
				allowExecutorMessages: { active: boolean };
				allowClientMessages: { active: boolean };
			}

			interface MapSettings {
				active: boolean;
				sectors: { active: boolean };
				parking: { active: boolean };
				showPerformersOnMap: { active: boolean };
				showOrdersOnMap: { active: boolean };
			}

			interface OrderSettings {
				active: boolean;
				allowFreeBroadcast: { active: boolean };
				ordersTabDefault: { active: boolean };
			}

			interface MenuFunctions {
				home: HomeSettings;
				archive: ArchiveSettings;
				orders: OrderSettings;
				map: MapSettings;
				message: MessageSettings;
				executor: ExecutorSettings;
			}

			interface MenuOrderProcessingFunctions {
				active: boolean;
				type: MenuTypeOrderProcessingFunctions;
			}

			interface OrderProcessingFunctions {
				menu: MenuOrderProcessingFunctions[];
				idleWaiting: { active: boolean };
				callClient: { active: boolean };
				navigation: { active: boolean };
				map: { active: boolean };
			}

			interface OwnOrderSettings {
				active: boolean;
				byPoints: { active: boolean };
				byTaximeter: { active: boolean };
				fixedAmount: { active: boolean };
			}

			interface StatusChange {
				statusOnlineOffline: { active: boolean };
				allowFree: { active: boolean };
				allowBusy: { active: boolean };
				allowDiner: { active: boolean };
				allowHome: { active: boolean };
			}

			/**  Widgets */
			interface SOS {
				active: boolean;
				time: number;
				callNumber: number;
			}

			/**  Widgets */
			interface OrderFilter {
				active: boolean;
				orderCost: { active: boolean };
				radius: { active: boolean };
				costPerKm: { active: boolean };
				routeLength: { active: boolean };
				supplySector: { active: boolean };
				destinationSector: { active: boolean };
				distanceToSupply: { active: boolean };
				ownOrdersOnly: { active: boolean };
				orderByHome: { active: boolean };
			}
			/**  Widgets */
			interface OrderSorting {
				active: boolean;
				orderCost: { active: boolean };
				costPerKm: { active: boolean };
				ownOrders: { active: boolean };
				orderDateTime: { active: boolean };
			}
			/**  Widgets */
			interface Parking {
				active: boolean;
				park: { active: boolean };
				unpark: { active: boolean };
				giveUpQueue: { active: boolean };
				changePark: { active: boolean };
			}
			/**  Widgets */
			interface Chain {
				active: boolean;
			}
			/**  Widgets */
			interface GpsSpoofing {
				active: boolean;
			}
			/**  Widgets */
			interface Communication {
				active: boolean;
				callDispatcher: { active: boolean; phones: string[] };
				requestCallback: { active: boolean };
			}

			/**  Widgets */
			interface RatingOrder {
				active: boolean;
				// Рейтинг +
				showRating: { active: boolean };
				showStars: { active: boolean };
				showPeople: { active: boolean };
			}

			/**  Widgets */
			interface Balance {
				active: boolean;
				showIncome: { active: boolean };
				allowAddToBalance: { active: boolean };
				allowWithdrawFromBalance: { active: boolean };
			}

			/**  Widgets */
			interface PerformerIncome {
				active: boolean;
				totalOrdersProcessed: {
					active: boolean;
				};
				totalIncome: {
					active: boolean;
					orderIncome: boolean;
					bonuses: boolean;
					bonus: boolean;
				};
				totalOrdersFulfilled: {
					active: boolean;
					cashOrders: boolean;
					cashlessOrders: boolean;
					cardPayment: boolean;
					compensated: boolean;
				};
				unfulfilledOrders: {
					active: boolean;
					clientCancellation: boolean;
					contractorFailure: boolean;
					otherReason: boolean;
				};
				expenses: {
					active: boolean;
					orderCommission: boolean;
					subscriptionFee: boolean;
					additionalPayment: boolean;
					penalties: boolean;
				};
				replenishmentBalance: {
					active: boolean;
					office: boolean;
					paymentSystems: boolean;
				};
			}
		}

		interface RegistrationFieldToggle {
			active: boolean;
			required: boolean;
		}

		interface RegistrationPassportSettings {
			active: boolean;
			series: RegistrationFieldToggle;
			number: RegistrationFieldToggle;
			issuedAt: RegistrationFieldToggle;
			issuedBy: RegistrationFieldToggle;
			birthPlace: RegistrationFieldToggle;
			residencePlace: RegistrationFieldToggle;
			livingAddress: RegistrationFieldToggle;
			taxId: RegistrationFieldToggle;
			files: RegistrationFieldToggle;
		}

		interface RegistrationDriverLicenseSettings {
			active: boolean;
			series: RegistrationFieldToggle;
			number: RegistrationFieldToggle;
			category: RegistrationFieldToggle;
			issuedAt: RegistrationFieldToggle;
			generalDrivingExperience: RegistrationFieldToggle;
			files: RegistrationFieldToggle;
		}

		interface RegistrationBankCardSettings {
			active: boolean;
			number: RegistrationFieldToggle;
			expiresAt: RegistrationFieldToggle;
			note: RegistrationFieldToggle;
		}

		interface RegistrationIndividualProprietorSettings {
			active: boolean;
			series: RegistrationFieldToggle;
			certificateNumber: RegistrationFieldToggle;
			registeredAt: RegistrationFieldToggle;
			usreou: RegistrationFieldToggle;
			iban: RegistrationFieldToggle;
			files: RegistrationFieldToggle;
		}

		interface RegistrationExecutorSettings {
			active: boolean;
			name: RegistrationFieldToggle;
			surname: RegistrationFieldToggle;
			fatherName: RegistrationFieldToggle;
			phones: RegistrationFieldToggle;
			emails: RegistrationFieldToggle;
			birthday: RegistrationFieldToggle;
			login: RegistrationFieldToggle;
			password: RegistrationFieldToggle;
			passport: RegistrationPassportSettings;
			driverLicense: RegistrationDriverLicenseSettings;
			bankCard: RegistrationBankCardSettings;
			individualProprietor: RegistrationIndividualProprietorSettings;
			personalFiles: RegistrationFieldToggle;
			licenseFiles: RegistrationFieldToggle;
			otherFiles: RegistrationFieldToggle;
		}

		interface RegistrationCarSettings {
			active: boolean;
			modelToBodyType: RegistrationFieldToggle;
			color: RegistrationFieldToggle;
			seats: RegistrationFieldToggle;
			manufactureYear: RegistrationFieldToggle;
			registrationNumber: RegistrationFieldToggle;
			vehicleNumber: RegistrationFieldToggle;
			transportOwnership: RegistrationFieldToggle;
			registrationCertificate: RegistrationFieldToggle;
			licenseCard: RegistrationFieldToggle;
			avatarFiles: RegistrationFieldToggle;
			registrationCertificateFiles: RegistrationFieldToggle;
			licenseCardFiles: RegistrationFieldToggle;
			insuranceFiles: RegistrationFieldToggle;
			otherFiles: RegistrationFieldToggle;
		}

		interface RegistrationSettings {
			active: boolean;
			executor: RegistrationExecutorSettings;
			car: RegistrationCarSettings;
		}
	}
}

export default ExecutorApp;
